/**
 * `ClockPort`（索引 Issue #498 の段階 4）— **時刻をどこから取るか**。
 *
 * | メソッド | 中身 |
 * |---|---|
 * | `ms()` | 今の位置 |
 * | `seek(ms)` / `play(rate)` / `pause()` / `paused()` / `rate()` | 動かす |
 * | `duration()` | 全体の長さ（分からなければ 0） |
 * | `volume()` / `setVolume(v)` | 元の音の大きさ（0〜1）。素材を持たない時計は何もしない |
 * | `subscribe(fn)` | 位置が変わった・再生 / 一時停止したら呼ぶ |
 *
 * **コアは時計を読まない**ので、実時間はここから入る（プレビューは動画の `requestVideoFrameCallback`、
 * 書き出しは外からフレームを進める）。ここに置くのは宣言と、**手で進める時計**（テストと書き出し）だけ。
 */
import { definePort, createEmitter } from './define';

export const ClockPort = definePort('ClockPort', {
  ms: () => 0,
  seek: () => {},
  play: () => {},
  pause: () => {},
  paused: () => true,
  rate: () => 1,
  duration: () => 0,
  volume: () => 1,
  setVolume: () => {},
  subscribe: () => () => {},
});

/**
 * 手で進める時計（書き出しの render モードとテスト）。実時間を読まないので**コアと同じく決定論的**。
 */
export function createManualClock({ ms = 0, durationMs = 0 } = {}) {
  const emitter = createEmitter();
  let at = ms;
  let playing = false;
  let speed = 1;
  const set = (v) => { at = Math.max(0, Math.round(v)); emitter.emit(at); };
  return {
    ...ClockPort.create({
      ms: () => at,
      seek: set,
      play: (r = 1) => { playing = true; speed = r; emitter.emit(at); },
      pause: () => { playing = false; speed = 1; emitter.emit(at); },
      paused: () => !playing,
      rate: () => speed,
      duration: () => durationMs,
      subscribe: emitter.subscribe,
    }),
    /** そのぶんだけ進める（再生の代わり） */
    advance: (deltaMs) => set(at + deltaMs),
  };
}

/**
 * 時計を編集セッションの再生位置に繋ぐ（`editor.transport` の `ms` と `playing` を時計に追従させる）。戻り値は外す関数。
 * 時計は位置が変わったときと、再生 / 一時停止したときに知らせる
 */
export function connectClock(editor, port) {
  if (!editor || !port) return () => {};
  const push = () => editor.seek(port.ms(), { playing: !port.paused() });
  push();
  return port.subscribe(push) ?? (() => {});
}
